import React, { useState, useRef, useEffect, useCallback, memo } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Loader2, Send } from "lucide-react";
import { config } from '../../config';
import ReactMarkdown from 'react-markdown';
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { v4 as uuidv4 } from 'uuid';
import SEOHead from '../../components/SEO/SEOHead';
import SEOFooter from '../../components/SEO/SEOFooter';
import { UnifiedSportsAdapter, SPORT_TYPES } from '../../services/sports/unifiedSportsAdapter';
import SimulatedChatDemo from '../../components/PreviewHomePanels/SimulatedChatDemo';

// Hybrid Data Grid - Premium feel with Brazilian warmth
const LiveDataGrid = ({ handleSignUp, variant = "full" }) => {
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFixtures = async () => {
      try {
        const games = await UnifiedSportsAdapter.getFeaturedGames(
          SPORT_TYPES.FOOTBALL,
          {
            leagueIds: [71, 39, 140, 2, 3, 253], // Brasileirão first
            limit: variant === "full" ? 8 : 4,
            includeFinished: false
          }
        );

        // If no games returned or empty array, throw error to trigger fallback
        if (!games || games.length === 0) {
          throw new Error('No games available from API');
        }

        const transformedGames = games.map(game => {
          const isLive = game.status === 'live' || game.isLive?.();
          const homeWinProb = 0.35 + Math.random() * 0.3;
          const drawProb = 0.25 + Math.random() * 0.15;
          const awayWinProb = 1 - homeWinProb - drawProb;

          let suggestion = "1X2";
          let probability = Math.max(homeWinProb, drawProb, awayWinProb);

          if (game.homeScore !== null && game.awayScore !== null) {
            const totalGoals = (game.homeScore || 0) + (game.awayScore || 0);
            suggestion = totalGoals > 1 ? "Over 2.5" : "Under 2.5";
            probability = 0.55 + Math.random() * 0.2;
          } else if (probability === homeWinProb && homeWinProb > 0.5) {
            suggestion = "Casa";
          } else if (probability === awayWinProb && awayWinProb > 0.5) {
            suggestion = "Fora";
          } else if (Math.random() > 0.5) {
            suggestion = "BTTS";
            probability = 0.48 + Math.random() * 0.25;
          }

          // Ensure we have full team data with logos
          const homeTeam = game.homeTeam || game.teams?.home || {};
          const awayTeam = game.awayTeam || game.teams?.away || {};

          return {
            ...game,
            homeTeam: {
              name: homeTeam.name || homeTeam.team_name || 'Time Casa',
              logo: homeTeam.logo || homeTeam.team_logo || homeTeam.icon || null,
              id: homeTeam.id || homeTeam.team_id
            },
            awayTeam: {
              name: awayTeam.name || awayTeam.team_name || 'Time Fora',
              logo: awayTeam.logo || awayTeam.team_logo || awayTeam.icon || null,
              id: awayTeam.id || awayTeam.team_id
            },
            league: game.league || { name: game.league_name || 'Liga' },
            probability,
            suggestion,
            expectedReturn: 1 / probability,
            status: isLive ? "LIVE" : "PENDING"
          };
        });

        setFixtures(transformedGames);
      } catch (error) {
        console.error('Error fetching fixtures:', error);
        // Static fallback data - no live games, just analysis stats
        const fallbackFixtures = [
          {
            id: 1,
            homeTeam: {
              name: "Flamengo",
              logo: "https://media.api-sports.io/football/teams/127.png"
            },
            awayTeam: {
              name: "Palmeiras",
              logo: "https://media.api-sports.io/football/teams/121.png"
            },
            league: { name: "Brasileirão Série A" },
            probability: 0.72,
            suggestion: "Over 2.5",
            expectedReturn: 1.84,
            status: "ANÁLISE"
          },
          {
            id: 2,
            homeTeam: {
              name: "Corinthians",
              logo: "https://media.api-sports.io/football/teams/131.png"
            },
            awayTeam: {
              name: "São Paulo",
              logo: "https://media.api-sports.io/football/teams/126.png"
            },
            league: { name: "Brasileirão Série A" },
            probability: 0.65,
            suggestion: "BTTS",
            expectedReturn: 1.95,
            status: "ANÁLISE"
          },
          {
            id: 3,
            homeTeam: {
              name: "Atlético-MG",
              logo: "https://media.api-sports.io/football/teams/1062.png"
            },
            awayTeam: {
              name: "Internacional",
              logo: "https://media.api-sports.io/football/teams/119.png"
            },
            league: { name: "Brasileirão Série A" },
            probability: 0.58,
            suggestion: "Under 2.5",
            expectedReturn: 2.12,
            status: "ANÁLISE"
          },
          {
            id: 4,
            homeTeam: {
              name: "Grêmio",
              logo: "https://media.api-sports.io/football/teams/130.png"
            },
            awayTeam: {
              name: "Cruzeiro",
              logo: "https://media.api-sports.io/football/teams/118.png"
            },
            league: { name: "Brasileirão Série A" },
            probability: 0.69,
            suggestion: "Casa",
            expectedReturn: 1.74,
            status: "ANÁLISE"
          },
          {
            id: 5,
            homeTeam: {
              name: "Fluminense",
              logo: "https://media.api-sports.io/football/teams/124.png"
            },
            awayTeam: {
              name: "Botafogo",
              logo: "https://media.api-sports.io/football/teams/120.png"
            },
            league: { name: "Brasileirão Série A" },
            probability: 0.71,
            suggestion: "Over 1.5",
            expectedReturn: 1.65,
            status: "ANÁLISE"
          },
          {
            id: 6,
            homeTeam: {
              name: "Santos",
              logo: "https://media.api-sports.io/football/teams/128.png"
            },
            awayTeam: {
              name: "Vasco",
              logo: "https://media.api-sports.io/football/teams/132.png"
            },
            league: { name: "Brasileirão Série B" },
            probability: 0.62,
            suggestion: "1X",
            expectedReturn: 1.88,
            status: "ANÁLISE"
          },
          {
            id: 7,
            homeTeam: {
              name: "Athletico-PR",
              logo: "https://media.api-sports.io/football/teams/134.png"
            },
            awayTeam: {
              name: "Fortaleza",
              logo: "https://media.api-sports.io/football/teams/154.png"
            },
            league: { name: "Brasileirão Série A" },
            probability: 0.66,
            suggestion: "BTTS",
            expectedReturn: 2.15,
            status: "ANÁLISE"
          },
          {
            id: 8,
            homeTeam: {
              name: "RB Bragantino",
              logo: "https://media.api-sports.io/football/teams/794.png"
            },
            awayTeam: {
              name: "Bahia",
              logo: "https://media.api-sports.io/football/teams/784.png"
            },
            league: { name: "Brasileirão Série A" },
            probability: 0.74,
            suggestion: "Over 2.5",
            expectedReturn: 1.52,
            status: "ANÁLISE"
          }
        ];
        setFixtures(fallbackFixtures);
      } finally {
        setLoading(false);
      }
    };

    fetchFixtures();
    const interval = setInterval(fetchFixtures, 120000);
    return () => clearInterval(interval);
  }, [variant]);

  if (loading) {
    return (
      <div className="p-8 text-center">
        <Loader2 className="w-5 h-5 animate-spin text-white/30 mx-auto" />
      </div>
    );
  }

  // Compact card view for hero section - ultra-minimal style
  if (variant === "compact") {
    return (
      <div className="space-y-2">
        {/* Desktop View */}
        <div className="hidden md:block space-y-2">
          {fixtures.slice(0, 4).map((fixture, index) => (
            <div
              key={fixture.id}
              onClick={() => handleSignUp()}
              className="group cursor-pointer transition-all"
            >
              <div className="flex items-center gap-4 p-3 border border-white/[0.05] hover:border-white/10 hover:bg-white/[0.02] transition-all">
                {/* Status Indicator */}
                <div className="flex-shrink-0">
                  {fixture.status === 'LIVE' ? (
                    <div className="flex items-center gap-1.5">
                      <span className="w-1 h-1 bg-emerald-500/70 rounded-full animate-pulse"></span>
                      <span className="text-[9px] text-emerald-500/50 font-mono">LIVE</span>
                    </div>
                  ) : (
                    <span className="text-[9px] text-white/20 font-mono">
                      {index + 1}.
                    </span>
                  )}
                </div>

                {/* Match Info with Logos */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {/* Home Team */}
                    <div className="flex items-center gap-1.5 flex-1">
                      {fixture.homeTeam?.logo && (
                        <img src={fixture.homeTeam.logo} alt="" className="w-4 h-4 object-contain opacity-60" />
                      )}
                      <span className="text-xs text-white/95 truncate">
                        {fixture.homeTeam?.name || fixture.teams?.home?.name}
                      </span>
                    </div>
                    {/* Score */}
                    {fixture.status === 'LIVE' && fixture.homeScore !== null && (
                      <span className="text-[10px] text-violet-400/60 font-mono">
                        {fixture.homeScore}-{fixture.awayScore}
                      </span>
                    )}
                    {/* Away Team */}
                    <div className="flex items-center gap-1.5 flex-1">
                      <span className="text-xs text-white/50 truncate">
                        {fixture.awayTeam?.name || fixture.teams?.away?.name}
                      </span>
                      {fixture.awayTeam?.logo && (
                        <img src={fixture.awayTeam.logo} alt="" className="w-4 h-4 object-contain opacity-40" />
                      )}
                    </div>
                  </div>
                  <div className="text-[9px] text-white/30 uppercase tracking-wider mt-0.5">
                    {fixture.league?.name || fixture.league}
                  </div>
                </div>

                {/* Prediction Data */}
                <div className="flex-shrink-0 text-right">
                  <div className="text-[11px] text-emerald-400/80 font-mono">
                    {((fixture.probability || 0.65) * 100).toFixed(0)}%
                  </div>
                  <div className="text-[9px] text-white/50">
                    {fixture.suggestion}
                  </div>
                </div>

                {/* Return */}
                <div className="flex-shrink-0">
                  <span className="text-xs text-violet-400/70 font-mono group-hover:text-violet-400/90 transition-all">
                    {(fixture.expectedReturn || 1.85).toFixed(2)}×
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile View */}
        <div className="md:hidden space-y-3">
          {fixtures.slice(0, 4).map((fixture, index) => (
            <div
              key={fixture.id}
              onClick={() => handleSignUp()}
              className="group cursor-pointer p-3 border border-white/[0.05] hover:border-white/10 hover:bg-white/[0.02] transition-all"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-[9px] text-white/20 font-mono">
                    {index + 1}.
                  </span>
                  {fixture.status === 'LIVE' && (
                    <div className="flex items-center gap-1">
                      <span className="w-1 h-1 bg-emerald-500/70 rounded-full animate-pulse"></span>
                      <span className="text-[9px] text-emerald-500/50">LIVE</span>
                    </div>
                  )}
                </div>
                <span className="text-[9px] text-white/30 uppercase tracking-wider">
                  {fixture.league?.name || fixture.league}
                </span>
              </div>

              {/* Teams */}
              <div className="space-y-1 mb-2">
                <div className="flex items-center gap-2">
                  {fixture.homeTeam?.logo && (
                    <img src={fixture.homeTeam.logo} alt="" className="w-4 h-4 object-contain opacity-60" />
                  )}
                  <span className="text-xs text-white/95">
                    {fixture.homeTeam?.name || fixture.teams?.home?.name}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {fixture.awayTeam?.logo && (
                    <img src={fixture.awayTeam.logo} alt="" className="w-4 h-4 object-contain opacity-40" />
                  )}
                  <span className="text-xs text-white/50">
                    {fixture.awayTeam?.name || fixture.teams?.away?.name}
                  </span>
                </div>
              </div>

              {/* Score if LIVE */}
              {fixture.status === 'LIVE' && fixture.homeScore !== null && (
                <div className="mb-2">
                  <span className="text-xs text-violet-400/60 font-mono">
                    {fixture.homeScore} - {fixture.awayScore}
                  </span>
                </div>
              )}

              {/* Stats */}
              <div className="flex items-center justify-between pt-2 border-t border-white/[0.03]">
                <div className="flex items-center gap-3">
                  <span className="text-[11px] text-emerald-400/80 font-mono">
                    {((fixture.probability || 0.65) * 100).toFixed(0)}%
                  </span>
                  <span className="text-[9px] text-white/50">
                    {fixture.suggestion}
                  </span>
                </div>
                <span className="text-xs text-violet-400/70 font-mono">
                  {(fixture.expectedReturn || 1.85).toFixed(2)}×
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Full data grid view - Financial terminal style
  return (
    <div>
      {/* Desktop Table View */}
      <div className="hidden md:block">
        {fixtures.slice(0, 6).map((fixture, index) => (
          <div
            key={fixture.id}
            className="group grid grid-cols-12 gap-4 px-4 py-3 hover:bg-white/[0.02] transition-all cursor-pointer border-b border-white/[0.05]"
            onClick={() => handleSignUp()}
          >
            {/* Index */}
            <div className="col-span-1">
              <span className="text-[10px] text-white/30 font-mono">
                {String(index + 1).padStart(2, '0')}
              </span>
            </div>

            {/* Match with Logos */}
            <div className="col-span-4">
              <div className="flex items-center gap-2">
                {fixture.homeTeam?.logo && (
                  <img src={fixture.homeTeam.logo} alt="" className="w-5 h-5 object-contain opacity-50" />
                )}
                <div className="flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs text-white/95">
                      {fixture.homeTeam?.name || fixture.teams?.home?.name}
                    </span>
                    <span className="text-[10px] text-violet-400/30">v</span>
                    <span className="text-xs text-white/70">
                      {fixture.awayTeam?.name || fixture.teams?.away?.name}
                    </span>
                  </div>
                  <div className="text-[9px] text-white/30 uppercase tracking-[0.15em] mt-0.5">
                    {fixture.league?.name || fixture.league}
                  </div>
                </div>
                {fixture.awayTeam?.logo && (
                  <img src={fixture.awayTeam.logo} alt="" className="w-5 h-5 object-contain opacity-30" />
                )}
              </div>
            </div>

            {/* Live Score */}
            <div className="col-span-1">
              {fixture.status === 'LIVE' && fixture.homeScore !== null ? (
                <span className="text-xs text-white/70 font-mono">
                  {fixture.homeScore}-{fixture.awayScore}
                </span>
              ) : (
                <span className="text-[10px] text-white/30">—</span>
              )}
            </div>

            {/* Confidence */}
            <div className="col-span-2">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1 bg-white/[0.05] overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-emerald-500/30 to-violet-500/30"
                    style={{ width: `${(fixture.probability || 0.65) * 100}%` }}
                  />
                </div>
                <span className="text-[11px] text-emerald-400/80 font-mono">
                  {((fixture.probability || 0.65) * 100).toFixed(0)}%
                </span>
              </div>
            </div>

            {/* Suggestion */}
            <div className="col-span-2">
              <span className="text-[11px] text-white/70 px-2 py-0.5 border border-violet-500/30 group-hover:border-violet-400/50 group-hover:text-violet-300/70 transition-all">
                {fixture.suggestion || "ANÁLISE"}
              </span>
            </div>

            {/* Expected Return */}
            <div className="col-span-1 text-right">
              <span className="text-xs text-violet-400/70 font-mono group-hover:text-violet-400/90 transition-all">
                {(fixture.expectedReturn || 1.85).toFixed(2)}×
              </span>
            </div>

            {/* Status */}
            <div className="col-span-1 text-right">
              {fixture.status === 'LIVE' ? (
                <div className="inline-flex items-center gap-1">
                  <span className="w-1 h-1 bg-emerald-500/70 rounded-full animate-pulse"></span>
                  <span className="text-[9px] text-emerald-500/50">LIVE</span>
                </div>
              ) : (
                <span className="text-[9px] text-white/30">PEND</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {fixtures.slice(0, 6).map((fixture, index) => (
          <div
            key={fixture.id}
            className="group p-4 border border-white/[0.05] hover:border-white/10 hover:bg-white/[0.02] transition-all cursor-pointer"
            onClick={() => handleSignUp()}
          >
            {/* Header Row */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-white/30 font-mono">
                  {String(index + 1).padStart(2, '0')}
                </span>
                {fixture.status === 'LIVE' && (
                  <div className="flex items-center gap-1">
                    <span className="w-1 h-1 bg-emerald-500/70 rounded-full animate-pulse"></span>
                    <span className="text-[9px] text-emerald-500/50">LIVE</span>
                  </div>
                )}
              </div>
              <span className="text-[9px] text-white/30 uppercase tracking-[0.15em]">
                {fixture.league?.name || fixture.league}
              </span>
            </div>

            {/* Teams */}
            <div className="space-y-2 mb-3">
              <div className="flex items-center gap-2">
                {fixture.homeTeam?.logo && (
                  <img src={fixture.homeTeam.logo} alt="" className="w-5 h-5 object-contain opacity-50" />
                )}
                <span className="text-sm text-white/95">
                  {fixture.homeTeam?.name || fixture.teams?.home?.name}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {fixture.awayTeam?.logo && (
                  <img src={fixture.awayTeam.logo} alt="" className="w-5 h-5 object-contain opacity-30" />
                )}
                <span className="text-sm text-white/70">
                  {fixture.awayTeam?.name || fixture.teams?.away?.name}
                </span>
              </div>
            </div>

            {/* Score if LIVE */}
            {fixture.status === 'LIVE' && fixture.homeScore !== null && (
              <div className="mb-3">
                <span className="text-sm text-violet-400/60 font-mono">
                  {fixture.homeScore} - {fixture.awayScore}
                </span>
              </div>
            )}

            {/* Stats Row */}
            <div className="flex items-center justify-between pt-3 border-t border-white/[0.05]">
              <div className="flex items-center gap-3">
                <div>
                  <span className="text-[9px] text-white/30 uppercase tracking-wider block mb-1">Confiança</span>
                  <span className="text-[13px] text-emerald-400/80 font-mono">
                    {((fixture.probability || 0.65) * 100).toFixed(0)}%
                  </span>
                </div>
                <div>
                  <span className="text-[9px] text-white/30 uppercase tracking-wider block mb-1">Palpite</span>
                  <span className="text-[11px] text-white/70">
                    {fixture.suggestion || "ANÁLISE"}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <span className="text-[9px] text-white/30 uppercase tracking-wider block mb-1">Retorno</span>
                <span className="text-sm text-violet-400/70 font-mono">
                  {(fixture.expectedReturn || 1.85).toFixed(2)}×
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const PtBrHomePageNew = memo(() => {
  useEffect(() => {
    document.title = "PickStar – Inteligência para Apostas Esportivas";
    const placeholder = document.getElementById('pt-br-placeholder');
    if (placeholder) placeholder.style.display = 'none';

    // Track that user came from /pt-br landing page for Facebook pixel
    sessionStorage.setItem('landingPage', '/pt-br');
  }, []);

  const SESSION_MESSAGE_LIMIT = 3;
  const [demoQuestion, setDemoQuestion] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [messages, setMessages] = useState([]);
  const [streamingResponse, setStreamingResponse] = useState(false);
  const [, setError] = useState(null);
  const [rateLimited, setRateLimited] = useState(false);
  const [sessionMessageCount, setSessionMessageCount] = useState(0);

  const navigate = useNavigate();
  const streamingMessageRef = useRef("");
  const sessionIdRef = useRef(uuidv4());
  const abortControllerRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Session management
  useEffect(() => {
    const existingSessionId = localStorage.getItem('guestSessionId');
    if (existingSessionId) {
      sessionIdRef.current = existingSessionId;
    } else {
      localStorage.setItem('guestSessionId', sessionIdRef.current);
    }

    const storedCount = localStorage.getItem(`sessionMessages_${sessionIdRef.current}`);
    const count = storedCount ? parseInt(storedCount) : 0;
    setSessionMessageCount(count);

    if (count >= SESSION_MESSAGE_LIMIT) {
      setRateLimited(true);
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      const scrollTimeout = setTimeout(() => {
        requestAnimationFrame(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        });
      }, 100);
      return () => clearTimeout(scrollTimeout);
    }
  }, [messages.length, isThinking, streamingResponse]);

  const incrementSessionCount = useCallback(() => {
    const newCount = sessionMessageCount + 1;
    setSessionMessageCount(newCount);
    localStorage.setItem(`sessionMessages_${sessionIdRef.current}`, newCount.toString());
    if (newCount >= SESSION_MESSAGE_LIMIT) {
      setRateLimited(true);
    }
    return newCount;
  }, [sessionMessageCount]);

  const saveGuestMessage = useCallback(async (message, options = {}) => {
    try {
      const guestMessagesRef = collection(db, "guest_messages");
      await addDoc(guestMessagesRef, {
        sessionId: sessionIdRef.current,
        content: message.text,
        sender: message.sender,
        timestamp: serverTimestamp(),
        isError: !!options.isError,
        errorType: options.errorType || null
      });
      return true;
    } catch (error) {
      console.error("Error saving guest message:", error);
      return false;
    }
  }, []);

  const getOrCreateGuestId = useCallback(() => {
    const existingGuestId = localStorage.getItem('guestId');
    if (existingGuestId) return existingGuestId;

    const newGuestId = `guest_${uuidv4()}`;
    localStorage.setItem('guestId', newGuestId);
    return newGuestId;
  }, []);

  const handleRealDemoSubmit = useCallback(async (e, customQuestion = null) => {
    if (e && e.preventDefault) e.preventDefault();
    const questionToSend = customQuestion || demoQuestion;
    if (!questionToSend.trim() || isProcessing) return;

    if (sessionMessageCount >= SESSION_MESSAGE_LIMIT) {
      setRateLimited(true);
      setError("Limite de demonstração atingido.");
      return;
    }

    setIsProcessing(true);
    setError(null);
    const userMessage = { sender: "user", text: questionToSend };
    abortControllerRef.current = new AbortController();

    try {
      setMessages(prevMessages => [...prevMessages, userMessage]);
      setIsThinking(true);
      incrementSessionCount();
      saveGuestMessage(userMessage).catch(err => console.error("Failed to save:", err));

      await new Promise(resolve => setTimeout(resolve, 800));

      const timestamp = Date.now();
      const randomSuffix = Math.random().toString(36).substring(2, 10);

      const requestPayload = {
        question: questionToSend,
        history: messages.map(msg => ({
          role: msg.sender === 'user' ? 'user' : 'assistant',
          content: msg.text
        })),
        language: "pt-BR",
        isGuest: true,
        metadata: {
          guestId: getOrCreateGuestId(),
          sessionId: `preview-session-${timestamp}-${randomSuffix}`,
          userId: `preview-user-${timestamp}-${randomSuffix}`,
          conversationId: `preview-conv-${timestamp}-${randomSuffix}`
        }
      };

      const response = await fetch(`${config.apiUrl}/ai/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        signal: abortControllerRef.current.signal,
        body: JSON.stringify(requestPayload),
      });

      if (!response.ok) {
        throw new Error(response.status === 429 ? 'Limite atingido.' : 'Erro na análise.');
      }

      setIsThinking(false);
      setStreamingResponse(true);

      let botResponse = "";
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        for (const line of chunk.split('\n')) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'content' && data.text) {
                botResponse += data.text;
                streamingMessageRef.current = botResponse;

                setMessages(prevMessages => {
                  const newMessages = [...prevMessages];
                  const lastIndex = newMessages.length - 1;

                  if (lastIndex >= 0 && newMessages[lastIndex].sender === 'bot' && newMessages[lastIndex].isStreaming) {
                    newMessages[lastIndex] = { sender: "bot", text: botResponse, isStreaming: true };
                  } else {
                    newMessages.push({ sender: "bot", text: botResponse, isStreaming: true });
                  }
                  return newMessages;
                });
              }
            } catch (e) {
              console.error('Parse error:', e);
            }
          }
        }
      }

      setMessages(prevMessages => {
        const newMessages = [...prevMessages];
        const lastIndex = newMessages.length - 1;
        if (lastIndex >= 0 && newMessages[lastIndex].sender === 'bot') {
          newMessages[lastIndex] = { sender: "bot", text: botResponse };
        } else {
          newMessages.push({ sender: "bot", text: botResponse });
        }
        return newMessages;
      });

      saveGuestMessage({ sender: "bot", text: botResponse });

      if (window.gtag) {
        window.gtag('event', 'guest_conversation', {
          'event_category': 'engagement',
          'event_label': questionToSend.substring(0, 30),
          'message_count': sessionMessageCount + 1
        });
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error("Error:", error);
      setError(error.message || "Sistema temporariamente indisponível.");

      setMessages(prevMessages => [
        ...prevMessages,
        { sender: "bot", text: "Sistema temporariamente indisponível." }
      ]);
    } finally {
      setIsProcessing(false);
      setIsThinking(false);
      setStreamingResponse(false);
      setDemoQuestion("");
    }
  }, [demoQuestion, isProcessing, sessionMessageCount, messages, incrementSessionCount, saveGuestMessage, getOrCreateGuestId]);

  const handleChipClick = useCallback((prompt) => {
    setDemoQuestion(prompt);
    setTimeout(() => handleRealDemoSubmit(null, prompt), 0);
  }, [handleRealDemoSubmit]);

  const handleBackToInput = useCallback(() => {
    setMessages([]);
    setDemoQuestion("");
    setError(null);
  }, []);

  const handleSignUp = useCallback(() => {
    if (window.gtag) {
      window.gtag('event', 'signup_from_preview', {
        'event_category': 'conversion',
        'event_label': messages.length > 0 ? 'after_demo' : 'direct',
        'session_id': sessionIdRef.current,
        'message_count': sessionMessageCount
      });
    }
    navigate('/home', { state: { isSignUp: true, language: 'BRL' } });
  }, [navigate, messages.length, sessionMessageCount]);

  const handleLogin = useCallback(() => {
    navigate('/home', { state: { isSignUp: false, language: 'BRL' } });
  }, [navigate]);

  const quickPicks = [
    { label: "Melhor jogo Brasileiro", prompt: "Qual a melhor aposta para os próximos jogos do Brasileirão?" },
    { label: "Análise Flamengo", prompt: "Análise completa e palpites para o próximo jogo do Flamengo" },
    { label: "Futebol hoje", prompt: "Quais são os melhores palpites de futebol para hoje com alta confiança?" },
    { label: "Como funciona", prompt: "Explique a metodologia de análise preditiva" }
  ];

  const ThinkingIndicator = memo(() => (
    <div className="flex justify-start">
      <div className="px-3 py-2 bg-white/[0.02] border-l-2 border-white/20">
        <span className="text-xs text-white/40">Processando...</span>
      </div>
    </div>
  ));

  const StreamingIndicator = memo(() => (
    <span className="inline-block w-0.5 h-3 bg-white/40 ml-0.5 animate-pulse"></span>
  ));

  return (
    <>
      <SEOHead />
      <div className="min-h-screen bg-black text-white overflow-x-hidden">

      {/* Ultra-Minimal Header */}
      <header className="fixed top-0 z-50 w-full bg-black border-b border-white/5">
        <div className="max-w-screen-xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-16">
              <h2 className="text-xs font-medium tracking-[0.25em] text-white/95">
                PICKSTAR
              </h2>
              <nav className="hidden md:flex items-center gap-8">
                <button
                  onClick={() => document.getElementById('terminal').scrollIntoView({ behavior: 'smooth' })}
                  className="text-[11px] text-white/50 hover:text-white/80 transition-all uppercase tracking-wider">
                  Terminal
                </button>
                <button
                  onClick={() => document.getElementById('analysis').scrollIntoView({ behavior: 'smooth' })}
                  className="text-[11px] text-white/50 hover:text-white/80 transition-all uppercase tracking-wider">
                  Análises
                </button>
                <button
                  onClick={() => document.getElementById('method').scrollIntoView({ behavior: 'smooth' })}
                  className="text-[11px] text-white/50 hover:text-white/80 transition-all uppercase tracking-wider">
                  Método
                </button>
                <button
                  onClick={() => document.getElementById('faq').scrollIntoView({ behavior: 'smooth' })}
                  className="text-[11px] text-white/50 hover:text-white/80 transition-all uppercase tracking-wider">
                  FAQ
                </button>
              </nav>
            </div>
            <div className="flex items-center gap-6">
              <div className="hidden md:flex items-center gap-3 text-[10px] text-white/30 font-mono">
                <span className="text-emerald-500/50">●</span>
                <span>ONLINE</span>
                <span className="text-white/20">|</span>
                <span>{new Date().toLocaleTimeString('pt-BR', {
                  hour: '2-digit',
                  minute: '2-digit',
                  timeZone: 'America/Sao_Paulo'
                })}</span>
                <span className="text-white/20">|</span>
                <span className="text-white/30">SÃO PAULO</span>
              </div>
              <button
                onClick={handleLogin}
                className="px-4 py-1.5 text-[11px] border border-white/10 text-white/70 hover:text-white hover:border-white/30 transition-all">
                ENTRAR
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Payment Info Strip - Subtle but visible */}
      <div className="fixed top-[57px] z-40 w-full bg-gradient-to-r from-emerald-500/[0.03] to-violet-500/[0.03] border-b border-white/[0.02]">
        <div className="max-w-screen-xl mx-auto px-6 py-1.5">
          <div className="flex items-center justify-center gap-6 text-xs text-white/60">
            <div className="flex items-center gap-2">
              <span className="text-emerald-400/70">✓</span>
              <span>Aceitamos PIX</span>
            </div>
            <span className="text-white/30">•</span>
            <div className="flex items-center gap-2">
              <span className="text-violet-400/70">✓</span>
              <span>Créditos sob demanda. Sem mensalidade obrigatória.</span>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section - Hybrid Approach */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-screen-xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-start">

            {/* Left Column - Headlines */}
            <div className="pt-8">
              <h1 className="text-5xl lg:text-7xl font-thin leading-[1.2] mb-4">
                <span className="block bg-gradient-to-r from-white to-emerald-400/70 bg-clip-text text-transparent pb-2">Inteligência</span>
                <span className="block text-white/95 pb-2">quantitativa</span>
                <span className="block bg-gradient-to-r from-white/50 to-violet-400/50 bg-clip-text text-transparent">para apostas</span>
              </h1>

              {/* Tagline */}
              <div className="mb-6 lg:mb-8">
                <p className="text-base lg:text-lg text-white/80 font-light leading-tight">
                  Converta horas de trabalho manual em{' '}
                  <span className="text-emerald-400/90 font-medium">segundos de clareza</span>
                </p>
              </div>

              <p className="text-[13px] text-white/50 mb-6 lg:mb-8 max-w-md leading-relaxed">
                <span className="text-emerald-400/60">#1</span> ferramenta avançada de pesquisa esportiva no Brasil.{' '}
                Sistema <span className="text-white/70">multi-agente</span> com <span className="text-white/70">centenas de dimensões</span> processadas instantaneamente.
              </p>

              {/* Key Metrics - Refined with color, tighter on mobile */}
              <div className="flex gap-6 lg:gap-10 mb-6 lg:mb-10 pb-6 lg:pb-8 border-b border-violet-500/10">
                <div>
                  <p className="text-2xl lg:text-3xl font-thin text-white/95 mb-1">74<span className="text-sm lg:text-lg text-emerald-400/50">%*</span></p>
                  <p className="text-[9px] text-white/30 uppercase tracking-[0.2em]">Precisão</p>
                </div>
                <div>
                  <p className="text-2xl lg:text-3xl font-thin text-white/95 mb-1">574<span className="text-sm lg:text-lg text-violet-400/50">K</span></p>
                  <p className="text-[9px] text-white/30 uppercase tracking-[0.2em]">Análises/Mês</p>
                </div>
                <div>
                  <p className="text-2xl lg:text-3xl font-thin mb-1 text-emerald-400/70">247</p>
                  <p className="text-[9px] text-white/30 uppercase tracking-[0.2em]">Ao vivo</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 lg:gap-4">
                <button
                  onClick={handleSignUp}
                  className="px-6 lg:px-8 py-2.5 lg:py-3 bg-gradient-to-r from-emerald-500/80 to-violet-500/80 text-white text-[10px] lg:text-[11px] font-medium tracking-[0.15em] hover:from-emerald-500 hover:to-violet-500 transition-all">
                  CRIAR CONTA GRÁTIS
                </button>
                <button
                  onClick={() => document.getElementById('terminal').scrollIntoView({ behavior: 'smooth' })}
                  className="px-5 lg:px-6 py-2.5 lg:py-3 border border-violet-500/30 text-[10px] lg:text-[11px] text-white/70 hover:text-violet-300/80 hover:border-violet-400/50 transition-all">
                  DEMONSTRAÇÃO
                </button>
              </div>
            </div>

            {/* Right Column - Simulated Chat Demo */}
            <div className="lg:pt-8">
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-emerald-400/80 lg:text-white/30 uppercase tracking-[0.2em] mb-1 font-bold lg:font-normal">Demo ao vivo</p>
                  <p className="text-[13px] text-white/70">Veja o PickStar AI em ação</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-emerald-500/50 rounded-full animate-pulse"></span>
                  <span className="text-[10px] text-emerald-500/30 font-mono">DEMO</span>
                </div>
              </div>
              <SimulatedChatDemo handleSignUp={handleSignUp} />
              <div className="mt-4 text-center">
                <button
                  onClick={handleSignUp}
                  className="text-[11px] text-white/40 hover:text-white transition-all">
                  Comece a conversar agora →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Daily Allowances Banner */}
      <section className="py-8 border-t border-white/10">
        <div className="max-w-screen-lg mx-auto px-6">
          <div className="relative group">
            {/* Subtle glow effect */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500/20 to-emerald-500/20 rounded-xl opacity-50 group-hover:opacity-100 blur-lg transition-opacity duration-500" />

            {/* Main banner card */}
            <div className="relative bg-white/[0.05] backdrop-blur-md rounded-xl border border-white/[0.15] overflow-hidden">
              {/* Top gradient accent */}
              <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />

              <div className="px-4 py-2.5 md:py-3">
                <div className="flex items-center justify-center gap-2 md:gap-3 flex-wrap">
                  {/* Info indicator */}
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse shadow-lg shadow-cyan-500/50" />
                    <span className="text-white/70 text-[10px] md:text-xs font-mono uppercase tracking-wider font-semibold">
                      Grátis Diariamente:
                    </span>
                  </div>

                  {/* Allowances */}
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="flex items-center gap-1.5">
                      <span className="text-white/90 text-xs md:text-sm font-bold">3</span>
                      <span className="text-white/70 text-[10px] md:text-xs font-medium">Chats IA</span>
                    </div>

                    <div className="w-px h-3 bg-white/[0.2]" />

                    <div className="flex items-center gap-1.5">
                      <span className="text-white/90 text-xs md:text-sm font-bold">1</span>
                      <span className="text-white/70 text-[10px] md:text-xs font-medium">X-Pick</span>
                    </div>

                    <div className="w-px h-3 bg-white/[0.2]" />

                    <div className="flex items-center gap-1.5">
                      <span className="text-white/90 text-xs md:text-sm font-bold">1</span>
                      <span className="text-white/70 text-[10px] md:text-xs font-medium">Previsão de Jogo Aprofundada</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom fade */}
              <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-emerald-500/40 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* How to Use Section - Quick Onboarding */}
      <section className="py-16 border-t border-white/10">
        <div className="max-w-screen-lg mx-auto px-6">
          <div className="mb-8">
            <p className="text-[10px] text-white/30 uppercase tracking-[0.2em] mb-2">Comece em segundos</p>
            <h2 className="text-2xl font-light text-white/95">Como usar o PickStar AI</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8 mb-12">
            <div className="group">
              <div className="text-[10px] text-emerald-400/60 font-mono mb-3">01</div>
              <h3 className="text-sm text-white/95 mb-2">Pergunta básica</h3>
              <p className="text-xs text-white/50 leading-relaxed">
                "Qual o melhor palpite para o jogo do Flamengo hoje?"
              </p>
              <p className="text-[9px] text-white/30 mt-2">
                Resposta direta com palpite principal
              </p>
            </div>

            <div className="group">
              <div className="text-[10px] text-violet-400/60 font-mono mb-3">02</div>
              <h3 className="text-sm text-white/95 mb-2">Análise comparativa</h3>
              <p className="text-xs text-white/50 leading-relaxed">
                "Compare as probabilidades de over 2.5 gols nos jogos do Brasileirão desta rodada"
              </p>
              <p className="text-[9px] text-white/30 mt-2">
                Múltiplas opções com métricas detalhadas
              </p>
            </div>

            <div className="group">
              <div className="text-[10px] text-emerald-400/60 font-mono mb-3">03</div>
              <h3 className="text-sm text-white/95 mb-2">Estratégia avançada</h3>
              <p className="text-xs text-white/50 leading-relaxed">
                "Encontre apostas de valor onde as odds estão acima do esperado para jogos com 70%+ de confiança"
              </p>
              <p className="text-[9px] text-white/30 mt-2">
                Análise profunda com filtros personalizados
              </p>
            </div>
          </div>

          {/* Why Better Than Generic AI */}
          <div className="border-l border-violet-500/20 pl-6">
            <h3 className="text-sm text-white/70 mb-3">Por que o PickStar supera IAs genéricas para apostas?</h3>
            <p className="text-xs text-white/50 leading-relaxed max-w-3xl">
              Construído por engenheiros de ML que são apostadores profissionais.
              Nossa engenharia de contexto especializada captura nuances de apostas que modelos genéricos ignoram –
              processando <span className="text-emerald-400/60">centenas de dimensões</span> em tempo real,
              incluindo odds ao vivo, histórico direto, condições de jogo e sentimento do mercado.
            </p>
          </div>
        </div>
      </section>

      {/* Live Analysis Grid - Full View */}
      <section id="analysis" className="py-16 border-t border-white/10">
        <div className="max-w-screen-xl mx-auto px-6">
          <div className="mb-8">
            <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Sistema de análise</p>
            <h2 className="text-2xl font-light">Partidas em monitoramento</h2>
          </div>

          <div className="border border-violet-500/10 bg-black/50">
            <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gradient-to-r from-emerald-500/[0.02] to-violet-500/[0.02] text-[9px] uppercase tracking-[0.2em] text-white/30 border-b border-violet-500/10">
              <span className="col-span-1">#</span>
              <span className="col-span-4">Partida</span>
              <span className="col-span-1">Placar</span>
              <span className="col-span-2">Confiança</span>
              <span className="col-span-2">Palpite</span>
              <span className="col-span-1 text-right">Odd</span>
              <span className="col-span-1 text-right">Status</span>
            </div>
            <LiveDataGrid handleSignUp={handleSignUp} variant="full" />
          </div>
        </div>
      </section>

      {/* Method Section - Concise */}
      <section id="method" className="py-16 border-t border-white/10">
        <div className="max-w-screen-xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            <div>
              <p className="text-[10px] text-white/30 uppercase tracking-wider mb-3">Tecnologia</p>
              <h3 className="text-xl font-light mb-3">Machine Learning</h3>
              <p className="text-xs text-white/50 leading-relaxed">
                Redes neurais desenvolvidas por engenheiros especializados, não tipsters duvidosos.
              </p>
            </div>
            <div>
              <p className="text-[10px] text-white/30 uppercase tracking-wider mb-3">Cobertura</p>
              <h3 className="text-xl font-light mb-3">10+ Esportes</h3>
              <p className="text-xs text-white/50 leading-relaxed">
                Hub de dados ao vivo: Futebol, MLB, NFL, NBA, NCAAF, NCAAB, WNBA, MMA, Vôlei.
              </p>
            </div>
            <div>
              <p className="text-[10px] text-white/30 uppercase tracking-wider mb-3">Alcance</p>
              <h3 className="text-xl font-light mb-3">100+ Ligas</h3>
              <p className="text-xs text-white/50 leading-relaxed">
                Brasileirão, Premier League, La Liga, NBA, UFC e centenas de competições globais.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section - Metrics Focused */}
      <section className="py-16 border-t border-white/10">
        <div className="max-w-screen-xl mx-auto px-6">
          <div className="mb-12">
            <p className="text-[10px] text-white/30 uppercase tracking-[0.2em] mb-2">Por que escolher PickStar</p>
            <h2 className="text-2xl font-light">Vantagens mensuráveis</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-8">
            <div className="border-l border-emerald-500/20 pl-4 md:pl-6">
              <div className="text-3xl md:text-4xl font-thin text-white/95 mb-2">10×</div>
              <div className="text-xs md:text-sm text-white/70 mb-1">Pesquisa Mais Rápida</div>
              <div className="text-[10px] md:text-xs text-white/50">Horas de análise em segundos</div>
            </div>

            <div className="border-l border-violet-500/20 pl-4 md:pl-6">
              <div className="text-3xl md:text-4xl font-thin text-white/95 mb-2">
                74%<span className="text-base md:text-lg text-emerald-400/50">*</span>
              </div>
              <div className="text-xs md:text-sm text-white/70 mb-1">Taxa de Acerto</div>
              <div className="text-[10px] md:text-xs text-white/50">Precisão histórica transparente</div>
            </div>

            <div className="border-l border-emerald-500/20 pl-4 md:pl-6">
              <div className="text-3xl md:text-4xl font-thin text-white/95 mb-2">24/7</div>
              <div className="text-xs md:text-sm text-white/70 mb-1">IA Disponível</div>
              <div className="text-[10px] md:text-xs text-white/50">Parceiro estratégico sempre online</div>
            </div>

            <div className="border-l border-violet-500/20 pl-4 md:pl-6">
              <div className="text-3xl md:text-4xl font-thin text-white/95 mb-2">100%</div>
              <div className="text-xs md:text-sm text-white/70 mb-1">Objetivo</div>
              <div className="text-[10px] md:text-xs text-white/50">Sem viés, pura matemática</div>
            </div>
          </div>

          <div className="text-[9px] text-white/30 italic">
            * Quando testado retrospectivamente em 4000 jogos de futebol recomendados na faixa de alta confiança pelo PickStar.
          </div>
        </div>
      </section>

      {/* AI Terminal - Streamlined */}
      <section id="terminal" className="py-16 border-t border-white/10">
        <div className="max-w-screen-lg mx-auto px-6">
          <div className="mb-8">
            <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Interface de consulta</p>
            <h2 className="text-2xl font-light">Terminal de análise</h2>
          </div>

          {messages.length === 0 ? (
            <div className="border border-white/10 bg-black/50">
              <form onSubmit={handleRealDemoSubmit} className="p-4 border-b border-white/10">
                <div className="relative">
                  <input
                    type="text"
                    value={demoQuestion}
                    onChange={(e) => setDemoQuestion(e.target.value)}
                    placeholder="Digite sua consulta..."
                    className="w-full bg-transparent text-sm text-white placeholder-white/20 outline-none pr-10"
                    disabled={isProcessing || rateLimited}
                  />
                  <button
                    type="submit"
                    disabled={isProcessing || !demoQuestion.trim() || rateLimited}
                    className="absolute right-0 top-0 text-white/30 hover:text-white disabled:opacity-20 transition-all">
                    <Send size={14} />
                  </button>
                </div>
              </form>

              <div className="p-4">
                <p className="text-[10px] text-white/30 uppercase tracking-wider mb-1">Comandos rápidos</p>
                <p className="text-[9px] text-emerald-400/50 mb-3">Clique em qualquer botão para testar</p>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                  {quickPicks.map((pick, index) => (
                    <button
                      key={index}
                      onClick={() => handleChipClick(pick.prompt)}
                      disabled={isProcessing}
                      className="text-xs text-left px-3 py-2 border border-white/10 hover:bg-white/[0.02] hover:border-white/20 transition-all disabled:opacity-30">
                      {pick.label}
                    </button>
                  ))}
                </div>
              </div>

              {sessionMessageCount > 0 && (
                <div className="px-4 pb-4 space-y-1">
                  <p className="text-[10px] text-white/30">
                    {sessionMessageCount}/{SESSION_MESSAGE_LIMIT} consultas de demonstração
                  </p>
                  <p className="text-[9px] text-white/20 italic">
                    Demonstração limitada. Resultados podem variar do produto completo.
                  </p>
                </div>
              )}

              {rateLimited && (
                <div className="p-4 bg-white/[0.02] border-t border-white/10">
                  <p className="text-xs text-white mb-3">Demonstração finalizada</p>
                  <button
                    onClick={handleSignUp}
                    className="px-4 py-2 bg-white text-black text-xs">
                    CRIAR CONTA
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="border border-white/10 bg-black/50">
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <button
                  onClick={handleBackToInput}
                  className="text-white/30 hover:text-white transition-all"
                  disabled={isProcessing}>
                  <ChevronLeft size={16} />
                </button>
                <p className="text-[10px] text-white/30 uppercase tracking-wider">Terminal</p>
                <span className="text-[10px] text-white/30">
                  {sessionMessageCount}/{SESSION_MESSAGE_LIMIT}
                </span>
              </div>

              <div className="p-4 max-h-[400px] overflow-y-auto space-y-3">
                {messages.map((msg, index) => (
                  <div key={index} className={`${msg.sender === 'user' ? 'text-right' : ''}`}>
                    <div className={`inline-block max-w-[80%] ${
                      msg.sender === 'user'
                        ? 'text-sm text-white/70'
                        : 'text-sm text-white/50 font-mono'
                    }`}>
                      {msg.sender === 'user' ? (
                        <>
                          <span className="text-[10px] text-white/30 block mb-1">CONSULTA</span>
                          {msg.text}
                        </>
                      ) : (
                        <>
                          <span className="text-[10px] text-white/30 block mb-1">RESPOSTA</span>
                          <div className="prose prose-invert prose-sm max-w-none">
                            <ReactMarkdown>{msg.text}</ReactMarkdown>
                            {streamingResponse && index === messages.length - 1 && <StreamingIndicator />}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
                {isThinking && <ThinkingIndicator />}
                <div ref={messagesEndRef} />
              </div>

              {messages.length > 0 && (
                <div className="p-4 border-t border-white/10">
                  <button
                    onClick={handleSignUp}
                    className="px-4 py-2 bg-white text-black text-xs w-full">
                    ACESSAR SISTEMA COMPLETO
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* FAQ Section - Trust Building */}
      <section id="faq" className="py-16 border-t border-white/10">
        <div className="max-w-screen-lg mx-auto px-6">
          <div className="mb-12">
            <p className="text-[10px] text-white/30 uppercase tracking-[0.2em] mb-2">Perguntas frequentes</p>
            <h2 className="text-2xl font-light">Dúvidas comuns</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-x-12 gap-y-8">
            <div className="border-l border-emerald-500/10 pl-6">
              <h3 className="text-sm text-white/95 mb-2">Vocês fazem apostas por mim?</h3>
              <p className="text-xs text-white/50 leading-relaxed">
                Não. Somos apenas análise e pesquisa. Não acessamos casas de apostas nem fazemos apostas. Fornecemos inteligência para suas decisões.
              </p>
            </div>

            <div className="border-l border-violet-500/10 pl-6">
              <h3 className="text-sm text-white/95 mb-2">Como funciona a taxa de 74% de acerto?</h3>
              <p className="text-xs text-white/50 leading-relaxed">
                Quando testado retrospectivamente em 4000 jogos de futebol recomendados na faixa de alta confiança pelo PickStar. Transparência total no histórico.
              </p>
            </div>

            <div className="border-l border-emerald-500/10 pl-6">
              <h3 className="text-sm text-white/95 mb-2">Preciso entender de estatística?</h3>
              <p className="text-xs text-white/50 leading-relaxed">
                Não. A IA traduz análises complexas em recomendações claras. Você conversa naturalmente e recebe insights acionáveis.
              </p>
            </div>

            <div className="border-l border-violet-500/10 pl-6">
              <h3 className="text-sm text-white/95 mb-2">Funciona para todos os esportes?</h3>
              <p className="text-xs text-white/50 leading-relaxed">
                Sim. Cobrimos 10+ esportes e 100+ ligas globais, incluindo Brasileirão, NFL, NBA, Premier League, UFC e muito mais.
              </p>
            </div>

            <div className="border-l border-emerald-500/10 pl-6">
              <h3 className="text-sm text-white/95 mb-2">Quanto custa o sistema?</h3>
              <p className="text-xs text-white/50 leading-relaxed">
                Grátis para começar! Conta básica tem uso diário gratuito. Compre Star Coins quando precisar mais análises - sem pressão, sem assinatura obrigatória.
              </p>
            </div>

            <div className="border-l border-violet-500/10 pl-6">
              <h3 className="text-sm text-white/95 mb-2">O que são Star Coins?</h3>
              <p className="text-xs text-white/50 leading-relaxed">
                Créditos para análises. Conta grátis recebe diariamente. Compre mais: R$ 5 = 12 coins, R$ 22 = 150 coins, R$ 44 = 650 coins. Nunca expiram.
              </p>
            </div>

            <div className="border-l border-emerald-500/10 pl-6">
              <h3 className="text-sm text-white/95 mb-2">Como funciona na prática?</h3>
              <p className="text-xs text-white/50 leading-relaxed">
                Pergunte "Análise Flamengo vs Palmeiras" → Receba breakdown detalhado com 73% confiança em Over 2.5 gols, odds sugeridas, estatísticas.
              </p>
            </div>

            <div className="border-l border-violet-500/10 pl-6">
              <h3 className="text-sm text-white/95 mb-2">É só para apostadores profissionais?</h3>
              <p className="text-xs text-white/50 leading-relaxed">
                Não! QuickPicks para iniciantes, análise profunda para experts. IA adapta respostas ao seu nível. Ferramenta dinâmica e personalizada.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA - Minimal */}
      <section className="py-16 border-t border-white/10">
        <div className="max-w-screen-md mx-auto px-6 text-center">
          <h2 className="text-2xl font-light mb-6">
            Comece a analisar com precisão
          </h2>

          <div className="flex justify-center gap-4 mb-8">
            <button
              onClick={handleSignUp}
              className="px-6 py-3 bg-white text-black text-xs tracking-wider hover:bg-white/90 transition-all">
              CRIAR CONTA GRÁTIS
            </button>
            <button
              onClick={handleLogin}
              className="px-6 py-3 border border-white/20 text-white text-xs tracking-wider hover:bg-white/[0.02] transition-all">
              JÁ TENHO CONTA
            </button>
          </div>

          <div className="flex justify-center gap-6 text-base text-white/70">
            <span>5.000+ usuários</span>
            <span>•</span>
            <span>Aceita PIX</span>
            <span>•</span>
            <span>Suporte 24/7</span>
          </div>
        </div>
      </section>

      <SEOFooter language="pt-BR" />
    </div>
    </>
  );
});

PtBrHomePageNew.displayName = 'PtBrHomePageNew';

export default PtBrHomePageNew;